import React, { useState, useRef } from "react";
import { Resource, useNotify, Title } from "react-admin";
import { Card, CardContent } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DatabaseUploadSection from "./project/components/DatabaseUploadSection";
import { useTokenOps } from "../components/TokenContext";
import { hostname } from "../providers/api";
import { useUpdateDom } from "../hooks/useUpdateDom";

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  content: {
    padding: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
}));

const DatabaseUploadPage = (props) => {
  useUpdateDom();
  const classes = useStyles();
  const notify = useNotify();
  const tokenOps = useTokenOps();
  const fileRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadOptions, setUploadOptions] = useState({
    processAndOrigin: false,
  });

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const uploadCalculatedData = async () => {
    if (!fileRef.current?.files?.[0]) {
      notify("Please select a file first", "warning");
      return;
    }

    const file = fileRef.current.files[0];
    const data = new FormData();
    data.append("file", file);

    // Mapeo de opciones a endpoints
    const endpointMap = {
      processAndOrigin: "uploadProcessAndOrigin",
    };

    // Determinar qué endpoints llamar según los checkboxes
    const endpoints = [];
    Object.keys(uploadOptions).forEach((key) => {
      if (uploadOptions[key]) {
        endpoints.push(endpointMap[key]);
      }
    });

    if (endpoints.length === 0) {
      notify("Please select at least one upload type", "warning");
      return;
    }

    setLoading(true);

    const successes = [];
    const errors = [];

    // TODO: Actualizar este endpoint cuando el backend esté disponible
    // Actualmente usa los mismos endpoints que Project, pero debería usar
    // el nuevo endpoint de carga masiva: ${hostname}/database/upload
    // Llamar a todos los endpoints de forma secuencial
    for (const endpoint of endpoints) {
      try {
        // NOTA: Este es un endpoint temporal. El backend debe proporcionar
        // un endpoint específico para la carga masiva a la base de datos.
        const response = await fetch(`${hostname}/database/${endpoint}`, {
          method: "POST",
          body: data,
          headers: {
            authorization: "Bearer " + tokenOps.account?.accessToken,
          },
        });

        if (response.status < 400) {
          successes.push(endpoint);
        } else {
          const errorText = await response.text();
          errors.push({ endpoint, error: errorText });
        }
      } catch (error) {
        errors.push({ endpoint, error: error.message });
      }
    }

    if (successes.length > 0) {
      notify(`Successfully uploaded: ${successes.join(", ")}`, "success");
    }

    if (errors.length > 0) {
      errors.forEach(({ endpoint, error }) => {
        notify(`Error uploading ${endpoint}: ${error}`, "error");
      });
    }

    setLoading(false);
  };

  return (
    <>
      <Title title="System database upload" />
      <Card className={classes.card}>
        <CardContent className={classes.content}>
          <DatabaseUploadSection
            fileRef={fileRef}
            selectedFile={selectedFile}
            handleFileChange={handleFileChange}
            uploadOptions={uploadOptions}
            setUploadOptions={setUploadOptions}
            uploadCalculatedData={uploadCalculatedData}
            loading={loading}
          />
        </CardContent>
      </Card>
    </>
  );
};

const databaseUploadResource = () => (
  <Resource
    name="database-upload"
    options={{ label: "System database upload", menuGroup: "Bcome" }}
    list={DatabaseUploadPage}
  />
);

export default databaseUploadResource;
